import React, { useEffect, useState } from "react";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { Button, Box } from "@mui/material";
import { Irai } from "../types/GxpTypes";
import IraiReview from "../components/IraiReview";
import ConfirmDialog from "../components/ConfirmDialog";
import { SubForm } from "./IraiRegisterSubForm";
import { useNavigate, useLocation } from "react-router-dom";
import { getJapanDateString } from "../utils/DateUtils";
import { useData } from "../utils/DataContext";
import { useAuth } from 'react-oidc-context';
import { apiDelete, apiPut, apiPostPdf, handleApiError } from '../utils/ApiClient';
import { useToast } from "../utils/ToastContext";
import { validateErPrCombination } from "../utils/ValidationUtils";
import { generateAndOpenPdf } from "../utils/PdfUtils";
import { createEmptyIrai } from "../utils/IraiUtils";
import LoadingOverlay from "../components/LoadingOverlay";

const IraiRegisterForm: React.FC = () => {
  const auth = useAuth();
  const methods = useForm<Irai>({
    mode: "onChange",
    defaultValues: {
      patientGender: 2, // デフォルトで女性を設定
      patientId: "", // Patient karute number
      lymphStsId: 1, // デフォルト値
      esStsId: 2,
      prStsId: 2,
      her2StsId: 1,
      diagnosisId: 2,
      ki67Per: 0.0,
      tclass: 1,
      nclass: 1,
      mclass: 1,
      menstruation: 3, // 不明
      tumorGrade: 4,  // 不明
    }
  });

  const [step, setStep] = React.useState(0);
  const [open, setOpen] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshData, removeIraiFromCache } = useData();
  const { showToast } = useToast(); // Toast表示機能を取得


  const handleNext = async () => {
    const currentData = methods.getValues();

    if (step === 0) {
      // patientNameを更新
      methods.setValue("patientName", `${currentData.patientLastName} ${currentData.patientFirstName}`);

      // 第1ステップのフィールドを検証
      const isValid = await methods.trigger(["patientLastName", "patientFirstName", "patientBirthday", "patientGender", "patientId"]);
      if (!isValid) return;

    } else if (step === 1) {
      // ER, PRのクロスフィールドバリデーション
      const erPrResult = validateErPrCombination(currentData.esStsId, currentData.prStsId);
      if (!erPrResult.isValid) {
        methods.setError("esStsId", { type: "manual", message: erPrResult.message });
        methods.setError("prStsId", { type: "manual", message: erPrResult.message });
        return;
      }
      methods.clearErrors(["esStsId", "prStsId"]);

      const isValid = await methods.trigger(["sampleDate", "diagnosisId", "esStsId", "prStsId", "her2StsId", "lymphStsId"]);
      if (!isValid) return;

    } else if (step === 2) {
      // 第3ステップのフィールドを検証 (ki67のバリデーションはFormFieldに組み込み済み)
      const isValid = await methods.trigger(["ki67Per", "tclass", "nclass", "mclass", "menstruation", "tumorGrade"]);
      if (!isValid) return;
    } else if (step === 3) {
      const isValid = await methods.trigger(["specimenId", "approvalDoctorName"]);
      if (!isValid) return;
    }

    setStep((prev) => prev + 1);
  };
  const handleBack = () => setStep((prev) => prev - 1);

  const handleCancel = () => {
    setOpen(true);
  };

  const handleConfirmCancel = async () => {
    setOpen(false);
    const data = methods.getValues();
    if (!data.id || data.id === "00000000") {
      // 新規作成中でまだ保存されていない場合はそのまま戻る
      navigate("/iraiList");
      return;
    }

    try {
      await apiDelete(`/deleteIrai/${data.id}`, auth.user?.access_token);
      // 強制更新の代わりに個別削除を使用してリストの状態を維持
      removeIraiFromCache(data.id);
    } catch (error) {
      console.error("Error deleting irai:", handleApiError(error, 'Deleting irai'));
    }
    navigate("/iraiList");
  };

  const handleCancelDialog = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    const data = methods.getValues();
    data.statusId = 0; // Set status to "入力中"
    data.formNo = (step + 1).toString(); // Save current sub-form number (1-4)
    data.organizationId = Number(auth.user!.profile['custom:organization_id'] || '0'); // Set organization ID if available
    try {
      await apiPut<Irai>("/updateIrai", data, auth.user?.access_token);
      await refreshData(true); // 強制更新
    } catch (error) {
      console.error("Error saving irai:", handleApiError(error, 'Saving irai'));
    }
    navigate("/iraiList");
  };

  const handleSubmitConfirm = async () => {
    setSubmitConfirmOpen(false);
    setIsProcessing(true); // 処理開始、画面を非活性に

    const data = methods.getValues();
    data.status = "依頼済";
    data.statusId = 1;
    data.iraiDate = getJapanDateString();
    data.organizationId = Number(auth.user!.profile['custom:organization_id'] || '0'); // Set organization ID if available
    data.patientName = data.patientLastName + " " + data.patientFirstName;

    try {
      const response = await apiPut<Irai>("/updateIrai", data, auth.user?.access_token);
      await new Promise(resolve => setTimeout(resolve, 200));

      try {
        const division = (auth.user?.profile?.['custom:division'] as string) || '';
        await generateAndOpenPdf(apiPostPdf, "/generateIraiForm", response.data, auth.user?.access_token, { division });
      } catch (pdfError) {
        console.error("Error generating irai PDF:", handleApiError(pdfError, 'Generating irai PDF'));
        showToast("依頼は送信されましたが、依頼書PDFの表示に失敗しました。一覧から再取得してください。", "warning");
      }

      await refreshData(true); // 強制更新
    } catch (error) {
      console.error("Error submitting irai:", handleApiError(error, 'Submitting irai'));
      showToast("依頼送信に失敗しました。しばらくしてから再度お試しください。", "error");
      setIsProcessing(false); // エラー時は処理終了
      return; // エラー時は遷移しない
    }

    setIsProcessing(false); // 処理完了
    navigate("/iraiList");
  };

  const handleSubmitCancel = () => {
    setSubmitConfirmOpen(false);
  };

  const handleSubmitForm: SubmitHandler<Irai> = async () => {
    // フォームのバリデーションが通った場合、確認ダイアログを表示
    setSubmitConfirmOpen(true);
  };

  useEffect(() => {
    const { irai } = location.state || {};
    const userName = auth.user?.profile?.name || "";

    if (irai) {
      // 既存データを編集する場合も、医師名フィールドは現在のログインユーザーで上書き
      const updatedIrai = {
        ...irai,
        approvalDoctorName: userName,
        dataApprovalDoctorName: "",
      };
      methods.reset(updatedIrai);
      // formNoがあればstepに反映
      if (irai.formNo && !isNaN(Number(irai.formNo))) {
        setStep(Number(irai.formNo) - 1);
      } else {
        setStep(0);
      }
    } else {
      const userId = auth.user?.profile?.email || "";
      methods.reset(createEmptyIrai(userName, userId));
      setStep(0);
    }
  }, [auth.user?.profile?.name, auth.user?.profile?.email, location.state, methods]);

  return (
    <Box sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      minHeight: "100vh",
      paddingTop: "30px",
      paddingX: 2,
    }}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmitForm)}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "650px",
            width: "65%",
            maxWidth: "800px",
          }}
        >
          <Box sx={{ flexGrow: 1, width: "100%" }}>
            {step === 0 && (
              <SubForm
                title="(1/5) 患者情報"
                subSets={[
                  "patientLastName",
                  "patientFirstName",
                  "patientBirthday",
                  "patientGender",
                  "patientId",
                ]}
                currentIrai={methods.getValues()}
              />
            )}
            {step === 1 && (
              <SubForm
                title="(2/5) 病理学情報１"
                subSets={[
                  "sampleDate",
                  "diagnosisId",
                  "esStsId",
                  "prStsId",
                  "her2StsId",
                  "lymphStsId",
                  "biopsyId",
                ]}

                currentIrai={methods.getValues()}
              />

            )}
            {step === 2 && (
              <SubForm
                title="(3/5) 病理学情報２"
                subSets={[
                  "ki67Per",
                  "tclass",
                  "nclass",
                  "mclass",
                  "menstruation",
                  "tumorGrade"]}

                currentIrai={methods.getValues()}
              />
            )}
            {step === 3 && (
              <SubForm
                title="(4/5) その他情報"
                subSets={[
                  "specimenId",
                  "submissionDate",
                  "approvalDoctorName",
                ]}
                currentIrai={methods.getValues()}
              />
            )}
            {step === 4 && <IraiReview />}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCancel}
              >
                依頼を取り消し
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                一時保存
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              {step > 0 && (
                <Button variant="contained" onClick={handleBack}>
                  戻る
                </Button>
              )}
              {step < 4 && (
                <Button variant="contained" onClick={handleNext}>
                  次へ
                </Button>
              )}
              {step === 4 && (
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={methods.formState.isSubmitting}
                >
                  依頼
                </Button>
              )}
            </Box>
          </Box>
          <ConfirmDialog
            open={open}
            message="入力された情報は削除されます。<br />本当に取り消してもよろしいですか？"
            onConfirm={handleConfirmCancel}
            onCancel={handleCancelDialog}
          />
          <ConfirmDialog
            open={submitConfirmOpen}
            title="依頼確認"
            message="入力された内容で依頼を送信します。<br />よろしいですか？<br /><b>※送信後に取り消すことはできません。</b>"
            confirmText="はい"
            cancelText="いいえ"
            confirmColor="error"
            onConfirm={handleSubmitConfirm}
            onCancel={handleSubmitCancel}
          />
        </form>
      </FormProvider>

      <LoadingOverlay
        open={isProcessing}
        message="依頼書を生成しています。しばらくお待ちください。"
      />
    </Box>
  );
};

export default IraiRegisterForm;
